import React, { Suspense, useMemo, useState } from "react";
import * as yup from "yup";
import { useTranslation } from "react-i18next";

import {
  HvAccordion,
  HvButton,
  HvDialogActions,
  HvDialogContent,
  HvDialogTitle,
  HvGrid,
  HvTypography,
} from "@hitachivantara/uikit-react-core";

import {
  IncidentData,
  usePriorities,
  useSeverity,
  useStatus,
  WorkflowActionData,
  WorkflowActionInfo,
} from "lib/api";
import Loading from "components/layout/Loading";
import { Formik, FormikHelpers } from "formik";
import { DateTimeStrings } from "components/workflow/QueryBuilder";
import MembersContent from "components/incident/AddEdit/Members/MembersContent";
import DetailsContent from "components/incident/AddEdit/Details/DetailsContent";
import FindCamera from "components/incident/AddEdit/Location/FindCamera";
import useStyles from "./styles";
import FindExistingIncident from "./FindExistingIncident";
import MapProperties from "./MapProperties";

export interface IncidentDialogContentProps {
  onCancel?: () => void;
  onApply?: (data: WorkflowActionData) => void;
  actionType?: WorkflowActionInfo;
  initialValues?: WorkflowActionData;
  selectedEventTypeId?: string;
}

interface AccordionState {
  similar: boolean;
  properties: boolean;
  members: boolean;
  camera: boolean;
  mapProperties: boolean;
}

const IncidentDialogContent: React.FC<IncidentDialogContentProps> = ({
  onCancel,
  onApply,
  actionType,
  initialValues,
  selectedEventTypeId,
}) => {
  const { t } = useTranslation("incidentDialog");

  const validationSchema = useMemo(
    () =>
      yup.object().shape(
        {
          proximity: yup
            .number()
            .typeError(t("validation.valueNotANumber"))
            .positive(t("validation.notPositiveNumber"))
            .when("timeframe", {
              is: (value: DateTimeStrings) => value !== undefined,
              then: yup
                .number()
                .typeError(t("validation.valueNotANumber"))
                .positive(t("validation.notPositiveNumber"))
                .required(t("validation.required")),
            }),
          timeframe: yup.object().when("proximity", {
            is: (value: string) => value !== "" && value !== undefined,
            then: yup.object().required(t("validation.required")),
          }),
          type: yup.number().required(t("validation.required")),
          status: yup.number().required(t("validation.required")),
          priority: yup.number().required(t("validation.required")),
          severity: yup.number().required(t("validation.required")),
          findCameras: yup.boolean(),
          radius: yup.number().when("findCameras", {
            is: true,
            then: yup
              .number()
              .typeError(t("validation.valueNotANumber"))
              .positive(t("validation.notPositiveNumber"))
              .required(t("validation.required")),
          }),
        },
        [["proximity", "timeframe"]]
      ),
    [t]
  );

  const { data: statusData } = useStatus({
    revalidateOnFocus: false,
    suspense: true,
  });
  const { data: priorityData } = usePriorities({
    revalidateOnFocus: false,
    suspense: true,
  });
  const { data: severityData } = useSeverity({
    revalidateOnFocus: false,
    suspense: true,
  });

  const defaultIncidentProperties = {
    status: statusData?.data.find((v) => v.status_is_default)?.pk_status_id,
    priority: priorityData?.data.find((v) => v.prt_is_default)?.pk_prt_id,
    severity: severityData?.data.find((v) => v.svrt_is_default)?.pk_svrt_id,
  };

  const secToTime = (value: number | undefined): Timeframe | undefined => {
    if (value === undefined) return value;
    const hours = Math.floor(value / 3600);
    const totalSeconds = value % 3600;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return {
      hours,
      minutes,
      seconds,
    };
  };

  const initialFormValues = useMemo((): Partial<IncidentDialogFormData> => {
    // for inc_nearyby_dist_in_miles, inc_occ_dur_in_secs and nearest_cameras_dist_in_miles,
    // the server always returns 0, even when saved with undefined, so we assume 0 is empty

    const findExistingIncident =
      (initialValues?.incident_data?.inc_nearyby_dist_in_miles != null &&
        initialValues.incident_data.inc_nearyby_dist_in_miles !== 0) ||
      (initialValues?.incident_data?.inc_occ_dur_in_secs != null &&
        initialValues.incident_data.inc_occ_dur_in_secs !== 0);

    const findCameras =
      initialValues?.incident_data?.nearest_cameras_dist_in_miles != null &&
      initialValues.incident_data.nearest_cameras_dist_in_miles !== 0;

    return {
      proximity: findExistingIncident
        ? initialValues?.incident_data?.inc_nearyby_dist_in_miles
        : undefined,
      timeframe: initialValues?.incident_data?.inc_occ_dur_in_secs
        ? secToTime(initialValues.incident_data.inc_occ_dur_in_secs)
        : undefined,
      type: initialValues?.incident_data?.incidentTypeId,
      status: defaultIncidentProperties.status,
      priority: defaultIncidentProperties.priority,
      severity: defaultIncidentProperties.severity,
      members: initialValues?.incident_data?.inc_watchlist,
      assignedUser: initialValues?.incident_data?.inc_assigned_user,
      findCameras,
      radius: findCameras
        ? initialValues?.incident_data?.nearest_cameras_dist_in_miles
        : undefined,
      mapProperties: initialValues?.attributes || {},
    };
  }, [
    defaultIncidentProperties.priority,
    defaultIncidentProperties.severity,
    defaultIncidentProperties.status,
    initialValues?.attributes,
    initialValues?.incident_data?.inc_assigned_user,
    initialValues?.incident_data?.inc_nearyby_dist_in_miles,
    initialValues?.incident_data?.inc_occ_dur_in_secs,
    initialValues?.incident_data?.inc_watchlist,
    initialValues?.incident_data?.incidentTypeId,
    initialValues?.incident_data?.nearest_cameras_dist_in_miles,
  ]);

  const [expandedState, setExpandedState] = useState<AccordionState>(() => ({
    similar:
      initialValues == null ||
      initialValues.incident_data == null ||
      (initialValues.incident_data.inc_nearyby_dist_in_miles != null &&
        initialValues.incident_data.inc_nearyby_dist_in_miles !== 0) ||
      (initialValues.incident_data.inc_occ_dur_in_secs != null &&
        initialValues.incident_data.inc_occ_dur_in_secs !== 0),
    properties: true,
    members:
      initialValues?.incident_data?.inc_watchlist != null &&
      initialValues.incident_data.inc_watchlist.length > 0,
    mapProperties:
      initialValues?.attributes != null &&
      Object.keys(initialValues.attributes).length > 0,
    camera:
      initialValues?.incident_data?.nearest_cameras_dist_in_miles != null &&
      initialValues.incident_data.nearest_cameras_dist_in_miles !== 0,
  }));

  const onCancelHandler = () => {
    onCancel?.();
  };
  const handleAccordionToggle = (
    key: "similar" | "properties" | "members" | "camera" | "mapProperties"
  ) => {
    const newValue = { ...expandedState } as AccordionState;
    newValue[key] = !newValue[key];
    setExpandedState(newValue);
  };

  const handleAll = (option: boolean) => {
    setExpandedState({
      similar: option,
      properties: option,
      members: option,
      camera: option,
      mapProperties: option,
    });
  };

  const timeToSec = (value: Timeframe | undefined) => {
    return value === undefined
      ? undefined
      : value.seconds + value.minutes * 60 + value.hours * 3600;
  };

  const onSubmitHandler = async (
    values: IncidentDialogFormData,
    actions: FormikHelpers<IncidentFormData>
  ) => {
    if (actionType != null) {
      const incidentData: IncidentData = {
        incidentTypeId: values.type,
        fk_priority_inc_id: values.priority,
        fk_severity_inc_id: values.severity,
        inc_nearyby_dist_in_miles: values.proximity
          ? Number(values.proximity)
          : undefined,
        inc_occ_dur_in_secs: timeToSec(values.timeframe),
        nearest_cameras_dist_in_miles: values.radius
          ? Number(values.radius)
          : undefined,
        inc_watchlist: values.members,
        inc_assigned_user: values.assignedUser as User,
      };
      const data: WorkflowActionData = {
        continueOnError: false,
        continueOnNoResult: false,
        isInvalid: false,
        ...initialValues,

        fk_action_name: actionType.action_name,
        incident_data: incidentData,
        attributes: values.mapProperties || {},
        // we'll need calculate this before sending to server
        exe_order: -1,
      };
      actions.setStatus({});
      onApply?.({
        ...initialValues,
        ...data,
      });
    }
  };

  const classes = useStyles();

  return (
    <Formik
      initialStatus={{ serverSideErrors: {} }}
      initialValues={initialFormValues as IncidentDialogFormData}
      validationSchema={validationSchema}
      onSubmit={onSubmitHandler}
    >
      {({ handleSubmit, isValid, isSubmitting, dirty }) => {
        return (
          <form onSubmit={handleSubmit} noValidate>
            <HvDialogTitle className={classes.dialogTitle}>
              <HvTypography component="h6" variant="xsTitle">
                {t("title")}
              </HvTypography>
              <div className={classes.buttonContainer}>
                <HvButton category="ghost" onClick={() => handleAll(false)}>
                  {t("buttons.closeAll")}
                </HvButton>
                <HvButton category="ghost" onClick={() => handleAll(true)}>
                  {t("buttons.expandAll")}
                </HvButton>
              </div>
            </HvDialogTitle>

            <HvDialogContent className={classes.dialogContent}>
              <Suspense
                fallback={
                  <Loading
                    delay={1000}
                    loadingLabel=""
                    className={classes.dialogContent}
                  />
                }
              >
                <HvGrid container spacing={1}>
                  <HvGrid item xs={12}>
                    <HvAccordion
                      headingLevel={2}
                      label={t("sections.existingIncident.title")}
                      onChange={() => handleAccordionToggle("similar")}
                      expanded={expandedState.similar}
                    >
                      <HvGrid container>
                        <HvGrid xs={12} item>
                          <HvTypography variant="normalText">
                            {t("sections.existingIncident.description")}
                          </HvTypography>
                        </HvGrid>
                        <HvGrid container xs={12} lg={9} item>
                          <FindExistingIncident />
                        </HvGrid>
                      </HvGrid>
                    </HvAccordion>
                  </HvGrid>
                  <HvGrid item xs={12}>
                    <HvAccordion
                      headingLevel={2}
                      label={t("sections.incidentProperties.title")}
                      onChange={() => handleAccordionToggle("properties")}
                      expanded={expandedState.properties}
                    >
                      <HvGrid container>
                        <HvGrid xs={12} item>
                          <HvTypography variant="normalText">
                            {t("sections.incidentProperties.description")}
                          </HvTypography>
                        </HvGrid>
                        <DetailsContent mode="selection" />
                      </HvGrid>
                    </HvAccordion>
                  </HvGrid>
                  <HvGrid item xs={12}>
                    <HvAccordion
                      headingLevel={2}
                      label={t("sections.members.title")}
                      onChange={() => handleAccordionToggle("members")}
                      expanded={expandedState.members}
                    >
                      <HvGrid container>
                        <HvGrid xs={12} item>
                          <HvTypography variant="normalText">
                            {t("sections.members.description")}
                          </HvTypography>
                        </HvGrid>
                        <MembersContent initialPageSize={5} />
                      </HvGrid>
                    </HvAccordion>
                  </HvGrid>
                  <HvGrid item xs={12}>
                    <HvAccordion
                      headingLevel={2}
                      label={t("sections.mapProperties.title")}
                      onChange={() => handleAccordionToggle("mapProperties")}
                      expanded={expandedState.mapProperties}
                    >
                      <HvGrid container>
                        <HvGrid xs={12} item>
                          <HvTypography variant="normalText">
                            {t("sections.mapProperties.description")}
                          </HvTypography>
                        </HvGrid>
                        <MapProperties
                          selectedEventTypeId={selectedEventTypeId}
                        />
                      </HvGrid>
                    </HvAccordion>
                  </HvGrid>
                  <HvGrid item xs={12}>
                    <HvAccordion
                      headingLevel={2}
                      label={t("sections.camera.title")}
                      onChange={() => handleAccordionToggle("camera")}
                      expanded={expandedState.camera}
                    >
                      <HvGrid container>
                        <HvGrid xs={12} item>
                          <HvTypography variant="normalText">
                            {t("sections.camera.description")}
                          </HvTypography>
                        </HvGrid>
                        <HvGrid container xs={12} sm={9} item>
                          <FindCamera mode="selection" />
                        </HvGrid>
                      </HvGrid>
                    </HvAccordion>
                  </HvGrid>
                </HvGrid>
              </Suspense>
            </HvDialogContent>

            <HvDialogActions>
              <HvButton
                category="ghost"
                type="submit"
                disabled={!isValid || isSubmitting || !dirty}
              >
                {t("buttons.save")}
              </HvButton>
              <HvButton category="ghost" onClick={onCancelHandler}>
                {t("buttons.cancel")}
              </HvButton>
            </HvDialogActions>
          </form>
        );
      }}
    </Formik>
  );
};

export default IncidentDialogContent;
